import { lowerCase } from "lodash";
import { Fragment, useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import LinkTextRender from "@shared/LinkTextRender";
import SmsMessage, { Media } from "./SmsMessage";
import WarningMessage from "../../SmsConversation/components/WarningMessage";

const LINK_TEXT_CLASSES =
  "tw-rounded-[8px] tw-bg-tinted-gray-50 tw-p-[10px] tw-inline-block tw-max-w-[600px] tw-whitespace-pre-wrap tw-overflow-wrap-anywhere";
const ERROR_CLASSES = {
  allFailed: "!tw-bg-semantic-red-5",
  someFailed: "!tw-bg-semantic-yellow-5",
};
const ERROR_STATUSES = ["undelivered", "failed"];

const ConversationMessage = ({ message, onClickImage, showTimeAgo, conversation }) => {
  const isPlaceTheme = document.body.classList.contains("place-theme");
  const isInbound = !conversation.conversationOwnerPersonUuids.includes(message.createdByUserPersonUuid);
  const isOutbound = !isInbound;
  const failedRecipients = useMemo(
    () =>
      message.deliveryStatuses
        .filter((s) => ERROR_STATUSES.includes(lowerCase(s.messageStatus)))
        .map((s) => ({
          alias: conversation.conversationUsers.find((u) => u.uuid === s.conversationUserUuid).alias,
          errorCode: s.errorCode,
        })),
    [message.deliveryStatuses, conversation.conversationUsers],
  );

  const failureType =
    (failedRecipients.length === message.deliveryStatuses.length - 1 && "allFailed") ||
    (failedRecipients.length > 0 && "someFailed") ||
    false;

  return (
    <Fragment key={message.id}>
      {showTimeAgo && (
        <div className="tw-text-12d tw-font-semibold tw-text-gray tw-text-center">
          {dayjs(message.dateCreated).fromNow()}
        </div>
      )}
      {isInbound &&
        message.attachments?.map((media, i) => (
          <SmsMessage
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            isInbound={isInbound}
            content={<Media media={media} onClick={() => onClickImage(message, i)} />}
            message={message}
            failedRecipients={failedRecipients}
            failureType={failureType}
          />
        ))}
      {isInbound && message.message && (
        <SmsMessage
          isInbound={isInbound}
          content={<LinkTextRender text={message.message} className={LINK_TEXT_CLASSES} />}
          message={message}
          failedRecipients={failedRecipients}
          failureType={failureType}
        />
      )}
      {isOutbound && message.message && (
        <SmsMessage
          isGabbiMessage={message.isGabbi}
          isInbound={isInbound}
          content={
            <LinkTextRender
              text={message.message}
              className={`${LINK_TEXT_CLASSES} ${
                failureType
                  ? ERROR_CLASSES[failureType]
                  : `${
                      message.isGabbi
                        ? "!tw-bg-[#f9f7fd] tw-text-[#3b1487] tw-border-solid tw-border-[2px] tw-border-semantic-purple-5"
                        : `${isPlaceTheme ? "!tw-bg-semantic-blue-5" : "!tw-bg-brivity-blue-20"}`
                    }`
              }`}
            />
          }
          message={message}
          failedRecipients={failedRecipients}
          failureType={failureType}
        />
      )}
      {isOutbound &&
        message.attachments?.map((media, i) => (
          <SmsMessage
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            isInbound={isInbound}
            content={<Media media={media} onClick={() => onClickImage(message, i)} />}
            message={message}
            failedRecipients={failedRecipients}
            failureType={failureType}
          />
        ))}
      {conversation?.isTrackingNumber && <WarningMessage />}
    </Fragment>
  );
};

ConversationMessage.propTypes = {
  conversation: PropTypes.shape().isRequired,
  message: PropTypes.shape().isRequired,
  onClickImage: PropTypes.func.isRequired,
  showTimeAgo: PropTypes.bool.isRequired,
};

export default ConversationMessage;
